<template>
  <container
    :isModal="true"
    @close-modal="closeScreen()"
    :loading="stateLoading"
  >
    <template v-slot:header>
      <h3>Importar Comprovante de Depósito AGESUL</h3>
    </template>
    <template v-slot:body>
      <form @submit.prevent="submitSave">
        <fieldset :disabled="stateLoading">
          <div class="p-formgrid p-grid">
            <div class="p-field p-col-12">
              <ul>
                <li>
                  Insira o Pedido AGESUL gerado por aqui (Word) e o comprovante
                  de pagamento real do banco.
                </li>
                <li>
                  As licenças-órgão são identificadas pelo marcador escondido no
                  próprio Pedido AGESUL — não edite o arquivo gerado.
                </li>
                <li>
                  O comprovante é anexado em todas as licenças-órgão listadas
                  no Pedido AGESUL. O valor depositado é lido automaticamente
                  do comprovante e dividido igualmente entre elas.
                </li>
              </ul>
            </div>
            <div class="p-field p-col-12">
              <input-file
                label="Pedido AGESUL (Word)"
                :id="stateId + '_pedidoAgesul'"
                accept=".docx"
                :hasDownload="false"
              />
            </div>
            <div class="p-field p-col-12">
              <input-file
                label="Comprovante de Pagamento"
                :id="stateId + '_comprovante'"
                accept="application/pdf,image/*"
                :hasDownload="false"
              />
            </div>
            <div class="p-col-12">
              <Button type="submit" class="p-button-rounded" label="Importar" />
            </div>
          </div>
        </fieldset>
      </form>
      <div v-if="importReturn">
        <div v-if="importReturn.sucesso && importReturn.sucesso.length">
          <div class="sub-title">
            <h4>LICENÇAS ATUALIZADAS COM SUCESSO</h4>
          </div>
          <ul>
            <li v-for="item in importReturn.sucesso" :key="item">
              {{ item }}
            </li>
          </ul>
        </div>
        <div v-if="importReturn.erros && importReturn.erros.length">
          <div class="sub-title">
            <h4>NÃO IMPORTADAS</h4>
          </div>
          <ul>
            <li v-for="erro in importReturn.erros" :key="erro.nome">
              <b>{{ erro.nome }}</b> : {{ erro.erro }}
            </li>
          </ul>
        </div>
      </div>
    </template>
  </container>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, computed, Ref } from "vue";
import { useLicencaOrgaoService } from "../../../modules/business/licenca/licencaOrgaoBusiness";
import { ImportacaoDocumentoReturn } from "../../../modules/business/veiculo/importacaoVeiculoBusiness";
import { useToast } from "../../../modules/message";

interface ImportacaoPedidoAgesulState {
  importReturn: ImportacaoDocumentoReturn | null;
  imported: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  props: {
    parentLoading: Boolean
  },
  setup(props, { emit }) {
    const state = reactive<ImportacaoPedidoAgesulState>({
      importReturn: null,
      imported: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || props.parentLoading;
    });

    const closeScreen = () => {
      emit("after-import", state.imported);
    };

    const fileInput = (suffix: string): HTMLInputElement | null => {
      return document.querySelector(
        "#" + state.stateId + suffix
      ) as HTMLInputElement;
    };

    const clearFiles = () => {
      [fileInput("_pedidoAgesul"), fileInput("_comprovante")].forEach(
        (file) => {
          if (file) {
            file.value = "";
          }
        }
      );
    };

    const submitSave = () => {
      state.importReturn = null;
      const pedidoAgesul = fileInput("_pedidoAgesul");
      const comprovante = fileInput("_comprovante");
      if (
        !pedidoAgesul ||
        !pedidoAgesul.files ||
        pedidoAgesul.files.length === 0
      ) {
        useToast().warning("É necessário o Pedido AGESUL.");
        return;
      }
      if (
        !comprovante ||
        !comprovante.files ||
        comprovante.files.length === 0
      ) {
        useToast().warning("É necessário o comprovante de pagamento.");
        return;
      }
      const formData = new FormData();
      formData.append("pedidoAgesul", pedidoAgesul.files[0]);
      formData.append("comprovante", comprovante.files[0]);

      const { importarPedidoAgesul, loading, data } = useLicencaOrgaoService();
      state.loadings.push(loading);
      importarPedidoAgesul(formData)
        .then(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          clearFiles();
          state.importReturn = data.value;
          if (!state.importReturn) {
            useToast().warning("Não foi possível realizar a importação.");
          } else {
            state.imported = !!state.importReturn.sucesso?.length;
            if (state.importReturn.erros && state.importReturn.erros.length) {
              useToast().warning(
                "Arquivo processado com erros. Verifique o retorno."
              );
            } else {
              useToast().success("Importação realizada com sucesso.");
            }
          }
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    return {
      stateLoading,
      closeScreen,
      submitSave,
      ...toRefs(state)
    };
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss" />
